/**
 * Overdue Filters Component
 * Filter controls for overdue invoices list
 * US-INV-004: AC-3 - Filters
 */

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Filter, X } from 'lucide-react';
import type { OverdueInvoiceFilters } from '@/types/invoice';

interface OverdueFiltersProps {
  onFilterChange: (filters: OverdueInvoiceFilters) => void;
  initialFilters?: OverdueInvoiceFilters;
}

export const OverdueFilters: React.FC<OverdueFiltersProps> = ({
  onFilterChange,
  initialFilters = {},
}) => {
  const [filters, setFilters] = useState<OverdueInvoiceFilters>(initialFilters);
  const [showFilters, setShowFilters] = useState(false);

  const handleInputChange = (field: keyof OverdueInvoiceFilters, value: string | number | undefined) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value === '' ? undefined : value,
    }));
  };

  const handleApplyFilters = () => {
    onFilterChange(filters);
  };

  const handleResetFilters = () => {
    const resetFilters: OverdueInvoiceFilters = {
      page: 0,
      size: 20,
    };
    setFilters(resetFilters);
    onFilterChange(resetFilters);
  };

  const hasActiveFilters = Object.keys(filters).some(
    (key) => key !== 'page' && key !== 'size' && filters[key as keyof OverdueInvoiceFilters] !== undefined
  );

  return (
    <div className="space-y-4">
      {/* Toggle Button */}
      <div className="flex items-center justify-between">
        <Button
          variant={showFilters ? 'default' : 'outline'}
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
          className="gap-2"
        >
          <Filter className="h-4 w-4" />
          {showFilters ? 'Hide Filters' : 'Show Filters'}
          {hasActiveFilters && !showFilters && (
            <span className="ml-2 px-2 py-0.5 text-xs bg-blue-500 text-white rounded-full">
              Active
            </span>
          )}
        </Button>

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={handleResetFilters} className="gap-2">
            <X className="h-4 w-4" />
            Clear All Filters
          </Button>
        )}
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Customer ID */}
              <div className="space-y-2">
                <Label htmlFor="customerId">Customer ID</Label>
                <Input
                  id="customerId"
                  type="number"
                  placeholder="Enter customer ID"
                  value={filters.customerId || ''}
                  onChange={(e) => handleInputChange('customerId', e.target.value ? Number(e.target.value) : undefined)}
                />
              </div>

              {/* Location */}
              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  type="text"
                  placeholder="Enter location"
                  value={filters.location || ''}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                />
              </div>

              {/* Min Amount */}
              <div className="space-y-2">
                <Label htmlFor="minAmount">Min Amount (₹)</Label>
                <Input
                  id="minAmount"
                  type="number"
                  placeholder="Minimum amount"
                  min="0"
                  step="100"
                  value={filters.minAmount || ''}
                  onChange={(e) => handleInputChange('minAmount', e.target.value ? Number(e.target.value) : undefined)}
                />
              </div>

              {/* Max Amount */}
              <div className="space-y-2">
                <Label htmlFor="maxAmount">Max Amount (₹)</Label>
                <Input
                  id="maxAmount"
                  type="number"
                  placeholder="Maximum amount"
                  min="0"
                  step="100"
                  value={filters.maxAmount || ''}
                  onChange={(e) => handleInputChange('maxAmount', e.target.value ? Number(e.target.value) : undefined)}
                />
              </div>

              {/* Min Days Overdue */}
              <div className="space-y-2">
                <Label htmlFor="minDaysOverdue">Min Days Overdue</Label>
                <Input
                  id="minDaysOverdue"
                  type="number"
                  placeholder="Minimum days"
                  min="0"
                  value={filters.minDaysOverdue || ''}
                  onChange={(e) => handleInputChange('minDaysOverdue', e.target.value ? Number(e.target.value) : undefined)}
                />
              </div>

              {/* Max Days Overdue */}
              <div className="space-y-2">
                <Label htmlFor="maxDaysOverdue">Max Days Overdue</Label>
                <Input
                  id="maxDaysOverdue"
                  type="number"
                  placeholder="Maximum days"
                  min="0"
                  value={filters.maxDaysOverdue || ''}
                  onChange={(e) => handleInputChange('maxDaysOverdue', e.target.value ? Number(e.target.value) : undefined)}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-4 mt-6">
              <Button onClick={handleApplyFilters} className="flex-1">
                Apply Filters
              </Button>
              <Button variant="outline" onClick={handleResetFilters} className="flex-1">
                Reset
              </Button>
            </div>

            {/* Active Filters Summary */}
            {hasActiveFilters && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium text-blue-900 mb-2">Active Filters:</p>
                <div className="flex flex-wrap gap-2">
                  {filters.customerId && (
                    <span className="text-xs px-2 py-1 bg-white border border-blue-200 rounded">
                      Customer ID: {filters.customerId}
                    </span>
                  )}
                  {filters.location && (
                    <span className="text-xs px-2 py-1 bg-white border border-blue-200 rounded">
                      Location: {filters.location}
                    </span>
                  )}
                  {filters.minAmount !== undefined && (
                    <span className="text-xs px-2 py-1 bg-white border border-blue-200 rounded">
                      Min Amount: ₹{filters.minAmount}
                    </span>
                  )}
                  {filters.maxAmount !== undefined && (
                    <span className="text-xs px-2 py-1 bg-white border border-blue-200 rounded">
                      Max Amount: ₹{filters.maxAmount}
                    </span>
                  )}
                  {filters.minDaysOverdue !== undefined && (
                    <span className="text-xs px-2 py-1 bg-white border border-blue-200 rounded">
                      Min Days: {filters.minDaysOverdue}
                    </span>
                  )}
                  {filters.maxDaysOverdue !== undefined && (
                    <span className="text-xs px-2 py-1 bg-white border border-blue-200 rounded">
                      Max Days: {filters.maxDaysOverdue}
                    </span>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
